import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "@/components/Reveal";

export const Kicker = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-2 font-poppins text-xs font-bold uppercase tracking-[0.22em] text-[#4E7D2C] ${className}`}>
    <span className="h-px w-8 bg-[#F5821F]" />
    {children}
  </span>
);

export const SectionHeading = ({ kicker, title, className = "", light = false }) => (
  <Reveal className={className}>
    {kicker && (
      <span className={`inline-flex items-center gap-2 font-poppins text-xs font-bold uppercase tracking-[0.22em] ${light ? "text-[#8CC63F]" : "text-[#4E7D2C]"}`}>
        <span className="h-px w-8 bg-[#F5821F]" />
        {kicker}
      </span>
    )}
    <h2 className={`mt-4 max-w-3xl font-montserrat text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl ${light ? "text-white" : "text-neutral-900"}`}>
      {title}
    </h2>
  </Reveal>
);

// Animated number counter that triggers on scroll
export const StatCounter = ({ value, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(value);
  const numMatch = String(value).match(/[\d]+/);

  useEffect(() => {
    if (!inView || !numMatch) return;
    const target = parseInt(numMatch[0], 10);
    const prefix = String(value).slice(0, numMatch.index);
    const suffix = String(value).slice(numMatch.index + numMatch[0].length);
    let start = 0;
    const dur = 1200;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = Math.round(eased * target);
      setDisplay(`${prefix}${cur}${suffix}`);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value, numMatch]);

  return <span ref={ref} className={className}>{numMatch ? display : value}</span>;
};

export const PageHero = ({ kicker, title, sub, image, children }) => (
  <section className="relative overflow-hidden bg-[#0A0A0A] pt-24 pb-20 text-white md:pt-32 md:pb-28" data-testid="page-hero">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
      </div>
    )}
    <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
        <span className="inline-flex items-center gap-2 font-poppins text-xs font-bold uppercase tracking-[0.22em] text-[#8CC63F]">
          <span className="h-px w-8 bg-[#F5821F]" />
          {kicker}
        </span>
        <h1 className="mt-5 max-w-4xl font-montserrat text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {sub && <p className="mt-6 max-w-2xl font-poppins text-lg leading-relaxed text-white/70">{sub}</p>}
        {children}
      </motion.div>
    </div>
  </section>
);
